'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import CloseIcon from '@/fo/icons/base/icons/cross24.svg';
import { utilImageLoader } from '@/fo/util/image-loader';

import { VideoPlayer } from '../components/VideoPlayer/VideoPlayer';

import { styles } from './UiKitSectionVideoBlock.styles';

type NativeElement = React.HTMLAttributes<HTMLDivElement>;
type Variants = VariantProps<typeof styles>;

export type Video = {
  desktop: string;
  mobile: string;
};

export type Poster = {
  desktop: string;
  mobile: string;
};

export type VideoBlock = {
  title: string;
  description?: string;
  video: Video;
  poster?: Poster;
};

export interface UiKitSectionVideoBlockProps {
  videoBlock: VideoBlock;
  title?: string;
  poster?: Poster;
  logo?: string;
  mode?: Variants['mode'];
  onClick?: NativeElement['onClick'];
}

export function UiKitSectionVideoBlock({
  videoBlock,
  logo,
  title,
  mode,
  ...props
}: UiKitSectionVideoBlockProps) {
  const slots = styles({ mode });
  const t = useTranslations();

  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const handlePlayClick = (event: React.MouseEvent) => {
    event.preventDefault();
    setIsVideoPlaying(true);
  };

  const videoSources = [
    {
      src: videoBlock.video.mobile,
      media: '(max-width:1024px)'
    },
    {
      src: videoBlock.video.desktop,
      media: '(min-width:1024px)'
    }
  ];

  const handleClose = () => {
    setIsVideoPlaying(false);
  };

  if (isVideoPlaying) {
    return (
      <div
        className={slots.base()}
        {...props}
      >
        {videoBlock.poster && (
          <div className={slots.poster({ className: 'blur-xs' })}>
            <picture>
              <source
                srcSet={utilImageLoader({
                  src: videoBlock.poster.mobile,
                  width: 375,
                  quality: 100
                })}
                media="(max-width: 767px)"
              />
              <Image
                className={slots.image()}
                src={utilImageLoader({
                  src: videoBlock.poster.desktop,
                  width: 1440,
                  quality: 100
                })}
                alt={videoBlock.title}
                fill
              />
            </picture>
          </div>
        )}
        <VideoPlayer
          title={title}
          sources={videoSources}
          width={1440}
          height={600}
          className={slots.video()}
          loop
          renderCloseButton={() => (
            <button
              onClick={handleClose}
              className={slots.closeButton()}
            >
              <CloseIcon className={slots.closeIcon()} />
            </button>
          )}
        />
      </div>
    );
  }

  return (
    <div
      className={slots.base()}
      {...props}
    >
      {videoBlock.poster ? (
        <picture>
          <source
            srcSet={utilImageLoader({
              src: videoBlock.poster.mobile,
              width: 375,
              quality: 100
            })}
            media="(max-width: 767px)"
          />
          <Image
            className={slots.image()}
            src={utilImageLoader({
              src: videoBlock.poster.desktop,
              width: 1440,
              quality: 100
            })}
            alt={videoBlock.title}
            fill
          />
        </picture>
      ) : (
        <video
          className={slots.poster()}
          muted
          title={videoBlock.title}
        >
          {videoSources.map((source, index) => (
            <source
              key={index}
              src={source.src}
              media={source.media}
            />
          ))}
        </video>
      )}

      <div className={slots.container()}>
        <div className={slots.wrap()}>
          {logo && (
            <Image
              src={logo}
              width={217}
              height={96}
              className={slots.logo()}
              alt={videoBlock.title}
            />
          )}
          <button
            onClick={handlePlayClick}
            className={slots.link()}
          >
            <span className={slots.linkIconWrap()}>
              <span className={slots.linkIconTriangle()} />
            </span>
            <span className={slots.linkText()}>{t('watch')}</span>
          </button>
          <div className={slots.title()}>{videoBlock.title}</div>
          {videoBlock.description && (
            <div className={slots.description()}>{videoBlock.description}</div>
          )}
        </div>
      </div>
    </div>
  );
}
